'use client';

import React, { useEffect, useState } from 'react';
import { ServiceIllustration } from './ServiceIllustration';

/**
 * Visuel d'une prestation, rempli dans son conteneur parent (`relative`).
 * - Photo réelle (opaque) → affichée en `cover`, plein cadre, avec léger zoom au survol.
 * - Illustration transparente → centrée en `contain` sur le fond du panneau.
 * - Aucun fichier → repli sur le dessin au trait `ServiceIllustration`.
 *
 * Le type (photo vs illustration) est déduit de la transparence des coins de
 * l'image (canvas), afin d'appliquer le bon traitement automatiquement.
 * Dépose simplement `<slug>.{png|jpg|svg|webp}` dans ce dossier.
 */
const EXTS = ['png', 'jpg', 'jpeg', 'svg', 'webp'] as const;

type Mode = 'cover' | 'contain';

function detectMode(img: HTMLImageElement): Mode {
  try {
    const size = 16;
    const c = document.createElement('canvas');
    c.width = size;
    c.height = size;
    const ctx = c.getContext('2d');
    if (!ctx) return 'cover';
    ctx.drawImage(img, 0, 0, size, size);
    const d = ctx.getImageData(0, 0, size, size).data;
    const last = size - 1;
    const cornerAlpha = [
      d[3], // haut-gauche
      d[last * 4 + 3], // haut-droite
      d[last * size * 4 + 3], // bas-gauche
      d[(last * size + last) * 4 + 3], // bas-droite
    ];
    // Un coin transparent → illustration détourée → traitement « contain ».
    return cornerAlpha.some((a) => a < 200) ? 'contain' : 'cover';
  } catch {
    return 'cover';
  }
}

export function ServiceVisual({ slug }: { slug: string }) {
  const [state, setState] = useState<{ src: string | null; mode: Mode }>({
    src: null,
    mode: 'contain',
  });

  useEffect(() => {
    let cancelled = false;
    let idx = 0;
    setState({ src: null, mode: 'contain' });

    const tryNext = () => {
      if (cancelled || idx >= EXTS.length) return;
      const url = `/assets/images/services/${slug}.${EXTS[idx]}`;
      const img = new Image();
      img.onload = () => {
        if (!cancelled) setState({ src: url, mode: detectMode(img) });
      };
      img.onerror = () => {
        idx += 1;
        tryNext();
      };
      img.src = url;
    };

    tryNext();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (state.src && state.mode === 'cover') {
    return (
      <img data-component-id="src/components/marketing/services/ServiceVisual.tsx:77:6" data-component-path="src/components/marketing/services/ServiceVisual.tsx" data-component-line="77" data-component-end-line="83" data-component-file="ServiceVisual.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22alt%22%3A%22%22%2C%22className%22%3A%22absolute%20inset-0%20h-full%20w-full%20object-cover%20transition-transform%20duration-700%20ease-out%20group-hover%3Ascale-105%22%7D"
        src={state.src}
        alt=""
        aria-hidden="true"
        draggable={false}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
      />
    );
  }

  if (state.src) {
    // Illustration détourée → centrée.
    return (
      <div data-component-id="src/components/marketing/services/ServiceVisual.tsx:90:6" data-component-path="src/components/marketing/services/ServiceVisual.tsx" data-component-line="90" data-component-end-line="98" data-component-file="ServiceVisual.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20flex%20items-center%20justify-center%20p-6%22%7D" className="absolute inset-0 flex items-center justify-center p-6">
        <img data-component-id="src/components/marketing/services/ServiceVisual.tsx:91:8" data-component-path="src/components/marketing/services/ServiceVisual.tsx" data-component-line="91" data-component-end-line="97" data-component-file="ServiceVisual.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22alt%22%3A%22%22%2C%22className%22%3A%22max-h-full%20w-auto%20object-contain%20transition-transform%20duration-500%20ease-out%20group-hover%3Ascale-%5B1.06%5D%22%7D"
          src={state.src}
          alt=""
          aria-hidden="true"
          draggable={false}
          className="max-h-full w-auto object-contain transition-transform duration-500 ease-out group-hover:scale-[1.06]"
        />
      </div>
    );
  }

  // Repli : dessin au trait monochrome.
  return (
    <div data-component-id="src/components/marketing/services/ServiceVisual.tsx:104:4" data-component-path="src/components/marketing/services/ServiceVisual.tsx" data-component-line="104" data-component-end-line="109" data-component-file="ServiceVisual.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20flex%20items-center%20justify-center%22%7D" className="absolute inset-0 flex items-center justify-center">
      <ServiceIllustration data-component-id="src/components/marketing/services/ServiceVisual.tsx:105:6" data-component-path="src/components/marketing/services/ServiceVisual.tsx" data-component-line="105" data-component-end-line="108" data-component-file="ServiceVisual.tsx" data-component-name="ServiceIllustration" data-component-content="%7B%22elementName%22%3A%22ServiceIllustration%22%2C%22className%22%3A%22h-%5B46%25%5D%20w-auto%20text-ink%20transition-transform%20duration-500%20ease-out%20group-hover%3Ascale-%5B1.06%5D%22%7D"
        slug={slug}
        className="h-[46%] w-auto text-ink transition-transform duration-500 ease-out group-hover:scale-[1.06]"
      />
    </div>
  );
}
